import { FC } from 'react';
import routes from 'constants/routes';
import { useLocation } from 'react-router';
import FEATURES from '@kontentino/kontentino-constants/Features';
import InsightsTrialExpired from 'app/components/subscriptionModals/InsightsTrialExpired';
import SubscriptionExpired from 'app/components/subscriptionModals/SubscriptionExpired';
import TrialExpired from 'app/components/subscriptionModals/TrialExpired';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import InsightsTrialStart from 'app/components/subscriptionModals/InsightsTrialStart';
import { FeaturesSlugs } from 'types/Account';
import { useMutation } from 'react-query';
import { requestFeatureTrial } from 'app/api/features';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import { useTranslation } from 'react-i18next';

type Props = {};

const SubscriptionModals: FC<Props> = () => {
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const { isTrialExpired, isSubscriptionExpired, featuresPermissions } =
    useSubscriptionInfo();

  // Suppress the global expired modals while the user is on the billing page.
  // Their "Choose your plan" CTA routes here so an expired account can
  // re-subscribe; re-rendering the modal over the billing route would bounce
  // the user straight back out, creating an infinite redirect loop (BUG-39).
  const isOnBillingRoute = location.pathname.includes(routes.SETTINGS.BILLING);

  const requestTrial = useMutation(requestFeatureTrial, {
    onSuccess() {
      document.location.reload();
    },
    onError(e: ApiClientError) {
      toast(e?.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
    },
  });

  function getFeature(slug: FeaturesSlugs) {
    return Object.values(featuresPermissions).find(
      (feature) => feature.slug === slug,
    );
  }

  function canHasFeatureTrial(slug: FeaturesSlugs) {
    const feature = getFeature(slug);

    return !feature?.hasAccess && !feature?.wasTrialed;
  }

  function hasFeatureTrialExpired(slug: FeaturesSlugs) {
    const feature = getFeature(slug);

    return !!(!feature?.hasAccess && feature?.wasTrialed);
  }

  return (
    <>
      <TrialExpired open={isTrialExpired && !isOnBillingRoute} />
      <SubscriptionExpired open={isSubscriptionExpired && !isOnBillingRoute} />

      {!isTrialExpired && !isSubscriptionExpired && (
        <>
          {location.pathname.includes(routes.INSIGHTS) &&
            !location.pathname.includes(routes.INSIGHTS_LITE) && (
              <>
                <InsightsTrialStart
                  open={canHasFeatureTrial(FEATURES.NEW_ANALYTICS)}
                  onStartTrialNow={() =>
                    requestTrial.mutate(FEATURES.NEW_ANALYTICS)
                  }
                />
                <InsightsTrialExpired
                  open={hasFeatureTrialExpired(FEATURES.NEW_ANALYTICS)}
                />
              </>
            )}
        </>
      )}
    </>
  );
};

export default SubscriptionModals;
